if (!customElements.get('product-engraving')) {
  customElements.define('product-engraving', class ProductEngraving extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      const listen = (element, type, handler) => element.addEventListener(type, handler, { signal: this.events.signal });
      this.modal = this.querySelector('dialog');
      this.trigger = this.querySelector('.product-customization-btn');
      this.input = this.querySelector('[data-engraving-input]');
      this.submit = this.querySelector('[data-engraving-submit]');
      this.error = this.querySelector('[data-engraving-error]');
      this.variants = JSON.parse(this.querySelector('[data-engraving-variants]').textContent);
      listen(this.trigger, 'click', () => this.open());
      listen(this.querySelector('[data-engraving-close]'), 'click', () => this.close());
      listen(this.modal, 'cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      listen(this.input, 'input', () => {
        this.input.setCustomValidity('');
        this.querySelector('.custom-engraving-preview p').textContent = this.input.value || this.input.placeholder;
        this.error.hidden = true;
      });
      listen(this.querySelector('[data-engraving-form]'), 'submit', (event) => this.addToCart(event));
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId === this.dataset.sectionId) this.updateVariant(data.variant?.id ?? null);
      });
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.events?.abort();
      clearTimeout(this.closeTimer);
      if (this.modal?.open) {
        this.modal.close();
        if (!this.wasScrollLocked) document.body.classList.remove('overflow-hidden');
      }
      this.initialized = false;
    }

    updateVariant(id) {
      const productForm = document.getElementById(this.dataset.productFormId);
      const selectedId = id === undefined ? productForm?.elements.namedItem('id')?.value : id;
      const variant = this.variants.find((item) => String(item.id) === String(selectedId));
      this.submit.disabled = this.busy || !variant?.available || this.dataset.engravingAvailable !== 'true';
      if (variant) this.querySelector('[data-engraving-total]').textContent = variant.total;
      return variant;
    }

    open() {
      clearTimeout(this.closeTimer);
      this.updateVariant();
      this.error.hidden = true;
      if (!this.modal.open) {
        this.wasScrollLocked = document.body.classList.contains('overflow-hidden');
        this.modal.showModal();
      }
      document.body.classList.add('overflow-hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.modal.classList.add('is-visible'));
      this.input.focus();
    }

    close(immediate = false) {
      clearTimeout(this.closeTimer);
      this.modal.classList.remove('is-visible');
      const finish = () => {
        this.modal.close();
        this.trigger.setAttribute('aria-expanded', 'false');
        if (!this.wasScrollLocked) document.body.classList.remove('overflow-hidden');
        this.trigger.focus();
      };
      if (immediate) finish();
      else this.closeTimer = setTimeout(finish, 200);
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy) return;
      // const text = this.input.value.trim();
      // this.input.setCustomValidity(/^[A-Za-z0-9 ]{1,10}$/.test(text) ? '' : 'Enter 1–10 characters using letters and numbers only.');

      const text = this.input.value;
      const isValid = /^[A-Za-z0-9 ]{1,16}$/.test(text) && text.trim().length > 0;

      this.input.setCustomValidity(
        isValid
          ? ''
          : 'Enter 1–16 characters using letters, numbers, and spaces. Spaces count toward the limit. Include at least one letter or number.'
      );
      if (!this.input.reportValidity()) return;
      const variant = this.updateVariant();
      if (this.submit.disabled || !variant) return;
      const form = document.getElementById(this.dataset.productFormId);
      const formData = new FormData(form);
      const properties = {};
      for (const [name, value] of formData) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      // Keep separate personalization requests from merging into an existing parent line.
      properties._engraving_group = crypto.randomUUID();
      properties.Engraving = text;
      const quantity = Number(formData.get('quantity') || 1);
      const parent = { id: variant.id, quantity, properties };
      if (formData.get('selling_plan')) parent.selling_plan = formData.get('selling_plan');
      const payload = {
        items: [parent, {
          id: Number(this.dataset.engravingVariantId), quantity,
          parent_id: variant.id, properties: { Engraving: text }
        }]
      };
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(this.trigger);
      }
      this.busy = true;
      this.submit.disabled = true;
      this.submit.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
      this.error.hidden = true;
      let added = false;
      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add engraving. Please try again.');
        added = true;
        this.close(true);
        if (!cart) {
          window.location.assign(routes.cart_url);
          return;
        }
        cart.classList.remove('is-empty');
        cart.renderContents({ ...result, ...result.items[0], sections: result.sections });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-engraving' });
      } catch (error) {
        // If section rendering fails after a successful add, show the cart without re-adding.
        if (added) window.location.assign(routes.cart_url);
        else {
          this.error.textContent = error.message || 'Unable to add engraving. Please try again.';
          this.error.hidden = false;
        }
      } finally {
        this.busy = false;
        this.submit.classList.remove('loading');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        this.updateVariant();
      }
    }
  });
}
